import { mapShortLocaleToFull } from "@/constants/locales";

function isSameCalendarDayInTimeZone(
  start: Date,
  end: Date,
  timeZone?: string,
): boolean {
  const dayFormat: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  };
  return (
    start.toLocaleDateString("en-CA", dayFormat) ===
    end.toLocaleDateString("en-CA", dayFormat)
  );
}

function includesTime(formatOptions: Intl.DateTimeFormatOptions): boolean {
  return (
    formatOptions.hour !== undefined ||
    formatOptions.minute !== undefined ||
    formatOptions.second !== undefined
  );
}

/**
 * Generic localized date formatting function with configurable options
 * Supports both single dates and date ranges
 */
function formatLocalizedDate(opts: {
  locale: string;
  start: string | Date;
  end?: string | Date;
  formatOptions?: Intl.DateTimeFormatOptions;
  /**
   * When the range starts and ends on the same day and the format includes a
   * time, join the two times instead of repeating the date for the end time.
   * Example: "11 Sept, 10:00-14:00" instead of "11 Sept, 10:00 - 11 Sept, 14:00".
   */
  collapseSameDayTime?: boolean;
}): string {
  const startDate = new Date(opts.start);
  const endDate = opts.end ? new Date(opts.end) : null;
  const resolvedLocale = mapShortLocaleToFull(opts.locale) || "en-gb";

  // Default format options (compact: "5 Dec")
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  };

  const formatOptions = opts.formatOptions || defaultOptions;
  const sameDay = endDate
    ? isSameCalendarDayInTimeZone(startDate, endDate, formatOptions.timeZone)
    : false;

  if (
    endDate &&
    sameDay &&
    opts.collapseSameDayTime &&
    includesTime(formatOptions)
  ) {
    const startPart = startDate.toLocaleDateString(
      resolvedLocale,
      formatOptions,
    );
    const endTime = endDate.toLocaleTimeString(resolvedLocale, {
      hour: formatOptions.hour,
      minute: formatOptions.minute,
      second: formatOptions.second,
      timeZone: formatOptions.timeZone,
    });
    return `${startPart}-${endTime}`;
  }

  if (endDate && (!sameDay || includesTime(formatOptions))) {
    return `${startDate.toLocaleDateString(resolvedLocale, formatOptions)} - ${endDate.toLocaleDateString(resolvedLocale, formatOptions)}`;
  }

  return startDate.toLocaleDateString(resolvedLocale, formatOptions);
}

/**
 * Format event dates with compact month format for lists/cards
 * Examples: "5 Dec", "5 Dec - 7 Dec", "11 Sept, 10:00-14:00"
 */
export function formatCompactEventDates(opts: {
  locale: string;
  start: string | Date;
  end?: string | Date;
  includeTime?: boolean;
  timeZone?: string;
}): string {
  return formatLocalizedDate({
    locale: opts.locale,
    start: opts.start,
    end: opts.end,
    collapseSameDayTime: true,
    formatOptions: {
      day: "numeric",
      month: "short",
      ...(opts.includeTime ? TIME_FORMAT_OPTIONS : {}),
      timeZone: opts.includeTime ? opts.timeZone : "UTC",
    },
  });
}

/**
 * Whether an indexed date string carries a time component. Ingestion
 * (discover-data-ingestion public_events.py) emits `start_date`/`end_date`
 * as date-only strings ("2026-07-10", parsed as UTC midnight), while
 * deadline/opens fields are full instants — this is the one place that
 * encodes how to tell them apart.
 */
export function hasTimeComponent(value: string): boolean {
  return value.includes("T");
}

/** Today as `YYYY-MM-DD` — the `max` a date-of-birth picker accepts. */
export function todayAsIsoDate(now: Date = new Date()): string {
  return convertDateToISOString(now);
}

const TIME_FORMAT_OPTIONS = {
  hour: "2-digit",
  minute: "2-digit",
} as const satisfies Intl.DateTimeFormatOptions;

export function resolveEventStart(opts: {
  date: string;
  time?: string | null;
}): { value: string; hasTime: boolean } {
  if (opts.time && hasTimeComponent(opts.time)) {
    return { value: opts.time, hasTime: true };
  }
  return { value: opts.date, hasTime: false };
}

export function resolveEventRange(opts: {
  startDate: string;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
}): { start: string; end?: string; includeTime: boolean } {
  const start = resolveEventStart({
    date: opts.startDate,
    time: opts.startTime,
  });
  const end = opts.endDate
    ? resolveEventStart({ date: opts.endDate, time: opts.endTime })
    : null;

  return {
    start: start.value,
    end: end?.value,
    includeTime: start.hasTime && (end === null || end.hasTime),
  };
}

/**
 * Whether an instant falls exactly on midnight in the given IANA zone (the
 * runtime's zone when omitted — match what the caller displays). Midnight
 * deadlines mean "by that day" rather than a specific time of day, so callers
 * use this to render just the date instead of a meaningless "00:00".
 */
export function isMidnightInZone(
  value: string | Date,
  timeZone?: string,
): boolean {
  return (
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone,
    }).format(new Date(value)) === "00:00"
  );
}

/**
 * Format single event date with full details for detailed views
 * Examples: "Wed, 05 Dec 2024", with includeTime: "Wed, 05 Dec 2024, 18:00"
 */
export function formatDetailedEventDate(opts: {
  locale: string;
  date: string | Date;
  includeTime?: boolean;
  timeZone?: string;
}): string {
  return formatLocalizedDate({
    locale: opts.locale,
    start: opts.date,
    formatOptions: {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(opts.includeTime ? TIME_FORMAT_OPTIONS : {}),
      timeZone: opts.timeZone,
    },
  });
}

/**
 * Format detailed event date range (used on event detail page)
 * Examples:
 * - Same day: "Wed, 05 Dec 2024", with time: "Wed, 05 Dec 2024, 10:00-14:00"
 * - Different days: "Wed, 05 Dec 2024 - Mon, 09 Dec 2024"
 */
export function formatDetailedEventDates(opts: {
  locale: string;
  start: string | Date;
  end?: string | Date | null;
  includeTime?: boolean;
  timeZone?: string;
}): string {
  return formatLocalizedDate({
    locale: opts.locale,
    start: opts.start,
    end: opts.end ?? undefined,
    collapseSameDayTime: true,
    formatOptions: {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(opts.includeTime ? TIME_FORMAT_OPTIONS : {}),
      timeZone: opts.includeTime ? opts.timeZone : "UTC",
    },
  });
}

/**
 * Convert Date object to ISO date string (YYYY-MM-DD)
 * Normalizes to local midnight to avoid timezone issues
 */
function convertDateToISOString(date: Date): string {
  const local = new Date(date);
  local.setHours(0, 0, 0, 0); // Normalize to midnight local time
  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, "0");
  const day = String(local.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
